import { getAuth, onAuthStateChanged } from 'firebase/auth'
import React, { useContext, useEffect, useState } from 'react'

import { useLocalStorage } from 'hooks/useLocalStorage'
import { useQueryClient } from 'hooks/useQueryClient'

import { functionCall, functions } from 'utils/firebase'
import { DEFAULT_OBJECT_STATE } from 'utils/stateDefaults'

import { useInitialRoute } from './useInitialRoute'

const AppContext = React.createContext({
  authUser: DEFAULT_OBJECT_STATE,
  firebaseAuthObject: getAuth(),
  ready: false,
  firstName: undefined as string | undefined,
  setFirstName: (name: string) => {},
  lastName: undefined as string | undefined,
  setLastName: (name: string) => {},
  initialRoute: '',
  setInitialRoute: (newRoute: string) => {},
})

const firebaseAuthObject = getAuth()

interface Props {
  children: React.ReactNode
}

const AuthProvider = ({ children }: Props) => {
  const queryClient = useQueryClient()

  const [authUser, setAuthUser] = useLocalStorage('auth', DEFAULT_OBJECT_STATE)
  const [ready, setReady, isAuthHydrated] = useLocalStorage('authReady', false)
  const [firstName, setFirstName] = useState<string | undefined>(undefined)
  const [lastName, setLastName] = useState<string | undefined>(undefined)
  const { initialRoute, setInitialRoute } = useInitialRoute()

  const authUserIsValid = !!authUser?.data && Object.keys(authUser?.data).length > 0

  useEffect(() => {
    return onAuthStateChanged(firebaseAuthObject, async (user) => {
      setAuthUser((prevState: typeof DEFAULT_OBJECT_STATE) =>
        user ? { ...prevState, data: user } : DEFAULT_OBJECT_STATE,
      )
    })
  }, [setAuthUser])

  useEffect(() => {
    if (authUserIsValid) {
      const fetchUserAfterValidLogin = async () => {
        const onUserLogin = functionCall(functions, 'onUserLogin')
        await onUserLogin()
        setReady(true)
      }

      if (!ready) {
        fetchUserAfterValidLogin()
      }
    }
  }, [authUser, authUserIsValid, ready, setReady, queryClient])

  // This resets the ready state to false on an auth 'logout'
  useEffect(() => {
    if (!authUserIsValid) setReady(false)
  }, [authUserIsValid, setReady])

  if (!isAuthHydrated) return null

  return (
    <AppContext.Provider
      value={{
        authUser,
        ready: isAuthHydrated,
        firebaseAuthObject,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        initialRoute,
        setInitialRoute,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

const useAuth = () => {
  const auth = useContext(AppContext)
  if (auth == null) {
    throw new Error('useAuth() called outside of a AuthProvider?')
  }
  return auth
}

export { AuthProvider, useAuth }
